import {
  CheckCircleRounded as SuccessIcon,
  ErrorRounded as FailedIcon,
  ExpandMoreRounded as ExpandIcon,
  HourglassTopRounded as InProgressIcon,
  KeyboardArrowDownRounded as RowOpenIcon,
  KeyboardArrowRightRounded as RowClosedIcon,
  RefreshRounded as RefreshIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { readableDate } from "common/dates";
import { toSentenceCase } from "common/strings";
import Component from "components/Component";
import Button from "components/common/Button";
import { useEffect, useState } from "react";
import * as adminService from "services/adminService";

// Amplify job statuses, labelled the way the Amplify console shows them.
const STATUS_LABELS = {
  SUCCEED: "Deployed",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
  RUNNING: "Running",
  PENDING: "Pending",
  PROVISIONING: "Provisioning",
};

const STATUS_COLOURS = {
  SUCCEED: "success",
  FAILED: "error",
  CANCELLED: "default",
  RUNNING: "warning",
  PENDING: "warning",
  PROVISIONING: "warning",
};

// Same icon set as the pipeline status badge; cancelled has none, like Stopped.
const STATUS_ICONS = {
  SUCCEED: <SuccessIcon />,
  FAILED: <FailedIcon />,
  RUNNING: <InProgressIcon />,
  PENDING: <InProgressIcon />,
  PROVISIONING: <InProgressIcon />,
};

const ACTIVE_STATUSES = new Set(["RUNNING", "PENDING", "PROVISIONING"]);

const isActive = (job) => ACTIVE_STATUSES.has(job?.status);

const formatDuration = (seconds) => {
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const COLUMN_COUNT = 6;

const StatusChip = ({ status }) => (
  <Chip
    color={STATUS_COLOURS[status] ?? "default"}
    icon={STATUS_ICONS[status]}
    label={STATUS_LABELS[status] ?? toSentenceCase(status ?? "Unknown")}
    size={"small"}
  />
);

const DurationCell = ({ job }) => {
  if (job.duration_seconds != null) {
    return formatDuration(job.duration_seconds);
  }
  if (isActive(job)) {
    return <Skeleton width={60} />;
  }
  return "—";
};

const CommitMessageCell = ({ job }) => (
  <Tooltip title={job.commit_id ?? ""}>
    <Typography
      variant={"body2"}
      sx={{
        maxWidth: 480,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {job.commit_message ?? "—"}
    </Typography>
  </Tooltip>
);

const LogBox = ({ step }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: "#1a1a1a",
        color: theme.brand.gray.gainsboro,
        fontFamily: "monospace",
        fontSize: "0.75rem",
        p: 1.5,
        borderRadius: 1,
        maxHeight: 400,
        overflowY: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      {step.log_error && (
        <Typography color={"error"} fontSize={"inherit"}>
          {`Could not fetch log: ${step.log_error}`}
        </Typography>
      )}
      {step.log_truncated && (
        <Typography
          color={"inherit"}
          fontSize={"inherit"}
          sx={{ opacity: 0.6 }}
        >
          {`… showing the last ${step.log_lines.length} lines`}
        </Typography>
      )}
      {step.log_lines.length === 0 && !step.log_error ? (
        <Typography
          color={"inherit"}
          fontSize={"inherit"}
          sx={{ opacity: 0.6 }}
        >
          {isActive(step)
            ? "Log is published when the step finishes."
            : "No log output."}
        </Typography>
      ) : (
        step.log_lines.map((line, i) => <div key={i}>{line}</div>)
      )}
    </Box>
  );
};

const StepAccordion = ({ step }) => (
  <Accordion
    defaultExpanded={step.status === "FAILED"}
    disableGutters
    elevation={0}
    sx={{ border: "none", "&:before": { display: "none" } }}
  >
    <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 0 }}>
      <Stack direction={"row"} spacing={2} alignItems={"center"}>
        <Typography variant={"body2"} sx={{ minWidth: 90 }}>
          {toSentenceCase(step.name ?? "Unknown")}
        </Typography>
        <StatusChip status={step.status} />
        {step.duration_seconds != null && (
          <Typography variant={"caption"} color={"text.secondary"}>
            {formatDuration(step.duration_seconds)}
          </Typography>
        )}
        {step.status_reason && (
          <Typography variant={"caption"} color={"error"}>
            {step.status_reason}
          </Typography>
        )}
      </Stack>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0 }}>
      <LogBox step={step} />
    </AccordionDetails>
  </Accordion>
);

const SiteDeploymentLogs = ({ jobId }) => {
  const {
    data,
    isFetching,
    isError,
  } = adminService.useGetSiteDeploymentLogsQuery(jobId);

  if (!data && isFetching) {
    return (
      <Typography variant={"body2"} color={"text.secondary"} sx={{ py: 1 }}>
        {"Loading logs…"}
      </Typography>
    );
  }
  if (isError || !data) {
    return (
      <Typography variant={"body2"} color={"error"} sx={{ py: 1 }}>
        {"Failed to load logs."}
      </Typography>
    );
  }
  return (
    <Stack direction={"column"} sx={{ py: 1 }}>
      {data.steps.map((step) => (
        <StepAccordion key={step.name} step={step} />
      ))}
    </Stack>
  );
};

const SiteDeploymentRow = ({ job }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? "none" : null } }}>
        <TableCell padding={"checkbox"}>
          <IconButton
            size={"small"}
            aria-label={open ? "Hide logs" : "Show logs"}
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? <RowOpenIcon /> : <RowClosedIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{`Deployment ${job.job_id}`}</TableCell>
        <TableCell>
          <StatusChip status={job.status} />
        </TableCell>
        <TableCell>
          <DurationCell job={job} />
        </TableCell>
        <TableCell>
          {job.started_at
            ? readableDate(job.started_at, { includeTime: true })
            : "—"}
        </TableCell>
        <TableCell>
          <CommitMessageCell job={job} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} sx={{ py: 0 }}>
          <Collapse in={open} unmountOnExit>
            <SiteDeploymentLogs jobId={job.job_id} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const IDLE_POLL_MS = 60000;
const ACTIVE_POLL_MS = 10000;

// Older pages are loaded on demand and never refreshed. Polling only ever
// replaces the first page, so once a second page is showing, auto refresh is
// paused rather than risk the two drifting out of step when a new build starts.
const useOlderPages = () => {
  const [pages, setPages] = useState([]);
  const [
    fetchPage,
    { isFetching },
  ] = adminService.useLazyGetSiteDeploymentsQuery();

  const loadMore = async (nextToken) => {
    const result = await fetchPage({ nextToken });
    if (result.data) {
      setPages((prev) => [...prev, result.data]);
    }
  };

  const reset = () => setPages([]);

  return { pages, loadMore, reset, isFetching };
};

const SiteDeployments = () => {
  // Poll quickly while a build is in flight, otherwise once a minute.
  const [pollingInterval, setPollingInterval] = useState(IDLE_POLL_MS);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { data, isFetching, refetch } = adminService.useGetSiteDeploymentsQuery(
    undefined,
    {
      pollingInterval: autoRefresh ? pollingInterval : 0,
    }
  );
  const older = useOlderPages();

  const firstPage = data?.jobs;
  const jobs = firstPage && [
    ...firstPage,
    ...older.pages.flatMap((page) => page.jobs),
  ];
  const lastPage = older.pages[older.pages.length - 1] ?? data;
  const nextToken = lastPage?.next_token;

  useEffect(() => {
    setPollingInterval(
      firstPage?.some(isActive) ? ACTIVE_POLL_MS : IDLE_POLL_MS
    );
  }, [firstPage]);

  const handleLoadMore = () => {
    setAutoRefresh(false);
    older.loadMore(nextToken);
  };

  const handleResume = () => {
    older.reset();
    setAutoRefresh(true);
    refetch();
  };

  return (
    <Component
      title={"Site Deployments"}
      subtitle={data?.branch ? `Amplify branch ${data.branch}` : undefined}
      isFetching={isFetching || older.isFetching}
      fadeOnFetch={false}
      options={
        <Button
          startIcon={<RefreshIcon />}
          size={"small"}
          onClick={handleResume}
          isDisabled={isFetching}
        >
          {"Refresh"}
        </Button>
      }
    >
      {!autoRefresh && (
        <Alert
          severity={"info"}
          sx={{ mb: 2 }}
          action={
            <Button size={"small"} onClick={handleResume}>
              {"Re-enable"}
            </Button>
          }
        >
          {"Auto refresh is paused while older deployments are shown."}
        </Alert>
      )}
      {!jobs && isFetching ? (
        <CircularProgress size={24} />
      ) : (
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell padding={"checkbox"} />
              <TableCell>{"Name"}</TableCell>
              <TableCell>{"Status"}</TableCell>
              <TableCell>{"Build duration"}</TableCell>
              <TableCell>{"Started at"}</TableCell>
              <TableCell>{"Commit message"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(jobs ?? []).map((job) => (
              <SiteDeploymentRow key={job.job_id} job={job} />
            ))}
            {jobs?.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Typography color={"text.secondary"}>
                    {"No deployments found."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {nextToken && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align={"center"}>
                  <Button
                    size={"small"}
                    onClick={handleLoadMore}
                    isLoading={older.isFetching}
                  >
                    {"Load more"}
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Component>
  );
};

export default SiteDeployments;
